import { useState, useEffect } from 'react'
import { Trash2, ChevronDown, ChevronUp, Clock, CheckCircle2, XCircle } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { RequestHistoryItem } from '../types/ingestion.types'

export function RequestHistory() {
  const [history, setHistory] = useState<RequestHistoryItem[]>([])
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [filterMethod, setFilterMethod] = useState<string>('all')
  const [filterStatus, setFilterStatus] = useState<string>('all')

  // Load history from localStorage
  useEffect(() => {
    const stored = localStorage.getItem('fh_request_history')
    if (stored) {
      try {
        setHistory(JSON.parse(stored))
      } catch {
        setHistory([])
      }
    }
  }, [])

  // Refresh history every second (in case other components add to it)
  useEffect(() => {
    const interval = setInterval(() => {
      const stored = localStorage.getItem('fh_request_history')
      if (stored) {
        try {
          setHistory(JSON.parse(stored))
        } catch {
          // Ignore
        }
      }
    }, 1000)

    return () => clearInterval(interval)
  }, [])

  const handleClearHistory = () => {
    if (confirm('Are you sure you want to clear all request history?')) {
      localStorage.removeItem('fh_request_history')
      setHistory([])
    }
  }

  const handleDeleteItem = (id: string) => {
    const updated = history.filter((item) => item.id !== id)
    localStorage.setItem('fh_request_history', JSON.stringify(updated))
    setHistory(updated)
    if (expandedId === id) {
      setExpandedId(null)
    }
  }

  const toggleExpand = (id: string) => {
    setExpandedId(expandedId === id ? null : id)
  }

  // Filter history
  const filteredHistory = history.filter((item) => {
    if (filterMethod !== 'all' && item.method !== filterMethod) return false
    if (filterStatus !== 'all') {
      const statusRange = filterStatus === '2xx' ? [200, 299] : filterStatus === '4xx' ? [400, 499] : [500, 599]
      if (item.status < statusRange[0] || item.status > statusRange[1]) return false
    }
    return true
  })

  const getStatusColor = (status: number) => {
    if (status >= 200 && status < 300) return 'text-green-600 bg-green-100'
    if (status >= 400 && status < 500) return 'text-yellow-600 bg-yellow-100'
    return 'text-red-600 bg-red-100'
  }

  const getMethodColor = (method: string) => {
    switch (method) {
      case 'GET':
        return 'text-blue-600 bg-blue-100'
      case 'POST':
        return 'text-green-600 bg-green-100'
      case 'PUT':
        return 'text-yellow-600 bg-yellow-100'
      case 'DELETE':
        return 'text-red-600 bg-red-100'
      default:
        return 'text-gray-600 bg-gray-100'
    }
  }

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp)
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    })
  }

  return (
    <div className="space-y-4">
      {/* Header with Filters */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <select
            value={filterMethod}
            onChange={(e) => setFilterMethod(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="all">All Methods</option>
            <option value="GET">GET</option>
            <option value="POST">POST</option>
            <option value="PUT">PUT</option>
            <option value="DELETE">DELETE</option>
          </select>

          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="all">All Status</option>
            <option value="2xx">2xx Success</option>
            <option value="4xx">4xx Client Error</option>
            <option value="5xx">5xx Server Error</option>
          </select>

          <div className="text-sm text-gray-600">
            Showing {filteredHistory.length} of {history.length} requests
          </div>
        </div>

        {history.length > 0 && (
          <button
            onClick={handleClearHistory}
            className="flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:text-red-700 hover:bg-red-50 rounded-md transition-colors"
          >
            <Trash2 className="h-4 w-4" />
            Clear All
          </button>
        )}
      </div>

      {/* History List */}
      {filteredHistory.length === 0 ? (
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-12 text-center">
          <Clock className="h-12 w-12 text-gray-400 mx-auto mb-3" />
          <h3 className="text-lg font-medium text-gray-900 mb-1">No Request History</h3>
          <p className="text-gray-500">
            {history.length === 0
              ? 'Start making API requests to see them here'
              : 'No requests match the selected filters'}
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {filteredHistory.map((item) => (
            <div
              key={item.id}
              className="border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow"
            >
              {/* Request Summary */}
              <div
                onClick={() => toggleExpand(item.id)}
                className="flex items-center gap-3 p-4 cursor-pointer hover:bg-gray-50"
              >
                <div className="flex-1 grid grid-cols-12 gap-3 items-center">
                  {/* Method */}
                  <div className="col-span-1">
                    <span className={cn('px-2 py-1 rounded text-xs font-medium', getMethodColor(item.method))}>
                      {item.method}
                    </span>
                  </div>

                  {/* Status */}
                  <div className="col-span-1">
                    <span className={cn('px-2 py-1 rounded text-xs font-medium', getStatusColor(item.status))}>
                      {item.status}
                    </span>
                  </div>

                  {/* Endpoint */}
                  <div className="col-span-5 font-mono text-sm text-gray-700 truncate">
                    {item.endpoint}
                  </div>

                  {/* Timestamp */}
                  <div className="col-span-3 text-sm text-gray-500">
                    {formatTimestamp(item.timestamp)}
                  </div>

                  {/* Duration */}
                  <div className="col-span-1 text-sm text-gray-500">
                    {item.duration}ms
                  </div>

                  {/* Success/Error Icon */}
                  <div className="col-span-1 flex justify-end">
                    {item.status >= 200 && item.status < 300 ? (
                      <CheckCircle2 className="h-5 w-5 text-green-600" />
                    ) : (
                      <XCircle className="h-5 w-5 text-red-600" />
                    )}
                  </div>
                </div>

                {/* Expand Icon */}
                <div className="flex items-center gap-2">
                  {expandedId === item.id ? (
                    <ChevronUp className="h-5 w-5 text-gray-400" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-gray-400" />
                  )}
                </div>
              </div>

              {/* Expanded Details */}
              {expandedId === item.id && (
                <div className="border-t border-gray-200 bg-gray-50 p-4">
                  <div className="grid grid-cols-2 gap-4">
                    {/* Request */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-medium text-gray-900">Request</h4>
                      </div>
                      <pre className="bg-white border border-gray-200 rounded p-3 text-xs overflow-auto max-h-64 font-mono">
                        {JSON.stringify(item.request, null, 2)}
                      </pre>
                    </div>

                    {/* Response */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-medium text-gray-900">Response</h4>
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            handleDeleteItem(item.id)
                          }}
                          className="text-sm text-red-600 hover:text-red-700 flex items-center gap-1"
                        >
                          <Trash2 className="h-4 w-4" />
                          Delete
                        </button>
                      </div>
                      <pre className="bg-white border border-gray-200 rounded p-3 text-xs overflow-auto max-h-64 font-mono">
                        {item.error || JSON.stringify(item.response, null, 2)}
                      </pre>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
